import {
	VelocityEnv,
	Wallet,
	SwapClientType,
	ConfirmationStrategy,
} from '@velocity-exchange/sdk';
import { useCallback } from 'react';
import { RpcEndpoint, EnvironmentConstants } from '@velocity-exchange/common';
import Env, {
	SubscriberType,
} from 'src/environmentVariables/EnvironmentVariables';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { TX_SENDER_TO_USE } from 'src/stores/DriftStore/constants';
import { GLOBAL_SETTINGS_CLIENT } from 'src/providers/settingsProvider';
import { generateAccountLoader } from 'src/utils/connection/generateAccountLoader';
import { generateConnection } from 'src/utils/connection/generateConnection';
import { generateDriftClient } from 'src/utils/connection/generateDriftClient';
import { createSwapClient } from 'src/utils/swap';

export const useGetClientsForNewConnection = () => {
	const wallet = useDriftStore((s) => s.wallet.current?.adapter);

	const getClientsForNewConnection = useCallback(
		(newRpc: RpcEndpoint, subscriberType: SubscriberType, env: VelocityEnv) => {
			const defaultWallet = Env.defaultWallet;

			// There's sometimes a race condition where the wallet is auto-connecting, puts itself in the store, but it's not properly set up / configured. This catches that and uses the default wallet instead. Another handler to protect against this race-condition in the useKeepWalletsInSync hook will make sure the wallet properly connects when it's ready
			const walletToUse =
				// @ts-ignore
				wallet && wallet.publicKey ? (wallet as Wallet) : defaultWallet;

			const newConnection = generateConnection(newRpc);
			const newAccountLoader = generateAccountLoader(newConnection);
			const newDriftClient = generateDriftClient(
				newConnection,
				newAccountLoader,
				walletToUse,
				Env.sdkEnv,
				subscriberType
			);

			const usingDevnet = env === 'devnet';

			const rpcs = EnvironmentConstants.rpcs[usingDevnet ? 'dev' : 'mainnet'];

			const additionalConnections = rpcs
				.filter(
					(rpc) => rpc.value !== newRpc.value && rpc.allowAdditionalConnection
				)
				.map((rpc) => generateConnection(rpc));

			const additionalTxSenderCallbacks = usingDevnet
				? []
				: [
						(rawTx: string) => {
							fetch(`/api/rpc/sendTransaction`, {
								headers: {
									'Content-Type': 'application/json',
								},
								method: 'POST',
								body: JSON.stringify({ tx: rawTx }),
							});
						},
					];

			newDriftClient.txSender = new TX_SENDER_TO_USE({
				connection: newConnection,
				wallet: walletToUse,
				additionalConnections,
				additionalTxSenderCallbacks,
				confirmationStrategy: ConfirmationStrategy.Combo,
				retrySleep: Env.txSenderRetryInterval,
				txHandler: newDriftClient.txHandler,
			});

			// Get the swap client type from settings, defaulting to titan
			const clientType: SwapClientType =
				GLOBAL_SETTINGS_CLIENT?.overallSettingsState?.swapClientType ?? 'titan';

			const newSwapClient = createSwapClient({
				clientType,
				connection: newConnection,
			});

			return {
				newConnection,
				newAccountLoader,
				newDriftClient,
				newSwapClient,
			};
		},
		[wallet]
	);

	return { getClientsForNewConnection };
};
